import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Card,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Grid,
    Typography,
    Tooltip
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { GET } from "APIclient";
import properties from "constants/ApplicationConstants";

const RATING_LEGEND = [
    { points: 1, rating: "Unsatisfactory", comment: "Performance is consistently non acceptable" },
    { points: 2, rating: "Below Expectations", comment: "Performance fails to meet job requirement on frequent basis" },
    { points: 3, rating: "Meets Expectations", comment: "Performance is regularly competent & dependable" },
    { points: 4, rating: "Exceeds Expectations", comment: "Performance is routinely above expectation" },
    { points: 5, rating: "Outstanding", comment: "Performance is consistently superior" },
];

const ROW_HEIGHT = 36;
const colStyle = { display: "flex", alignItems: "center", minHeight: ROW_HEIGHT };

const scoreSx = {
    height: ROW_HEIGHT,
    fontSize: "0.8rem",
    "& .MuiSelect-select": { py: "6px", color: "#000 !important" },
    "&.Mui-disabled .MuiSelect-select": { color: "#000 !important", WebkitTextFillColor: "#000 !important" },
};

const commentSx = {
    "& .MuiOutlinedInput-root": { height: ROW_HEIGHT, alignItems: "center" },
    "& .MuiInputBase-input": { py: "6px", fontSize: "0.8rem", color: "#000 !important", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000 !important", color: "#000 !important" },
};

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box p={0.5}>{children}</Box>}
        </div>
    );
}

function ViewEmployeeRatings() {
    const navigate = useNavigate();
    const { appraisalId, empId } = useParams();

    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [notification, setNotification] = useState({ open: false, message: "", type: "" });
    const [employeeData, setEmployeeData] = useState(null);
    const [appraisalData, setAppraisalData] = useState(null);
    const [managerId, setManagerId] = useState(null);

    useEffect(() => {
        const storedEmpId = localStorage.getItem("empId") || sessionStorage.getItem("empId");
        if (storedEmpId) {
            const parsedManagerId = parseInt(storedEmpId);
            setManagerId(parsedManagerId);
            fetchAppraisalDetails(appraisalId, empId, parsedManagerId);
        } else {
            showNotification("Manager ID not found", "error");
            setLoading(false);
        }
    }, [appraisalId, empId]);

    const fetchAppraisalDetails = async (appraisalId, employeeId, managerId) => {
        try {
            setLoading(true);
            const response = await GET(`manager/${appraisalId}/employee/${employeeId}/view?managerId=${managerId}`);
            if (response.data?.data) {
                const data = response.data.data;
                const nameParts = (data.employeeName || '').split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
                const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : '';

                const transformedData = {
                    employee: {
                        empId: data.empId,
                        firstName, middleName, lastName,
                        gender: data.gender || '',
                        maritalStatus: data.maritalStatus === 'unmarried' ? 'Unmarried' : 'Married' || '',
                        dateOfJoining: data.dateOfJoining || '',
                        totalExperience: data.totalExperience || '',
                        reportingSince: data.reportingSince || '',
                        empPermission: data.empPermission,
                    },
                    technicalPrimarySkills: data.technicalSkills?.primary || [],
                    technicalSecondarySkills: data.technicalSkills?.secondary || [],
                    competencyPrimarySkills: data.competencySkills?.primary || [],
                    competencySecondarySkills: data.competencySkills?.secondary || [],
                    questionnaire: data.questionnaire || [],
                };

                const hasAppraisalData =
                    transformedData.technicalPrimarySkills?.length > 0 ||
                    transformedData.technicalSecondarySkills?.length > 0 ||
                    transformedData.competencyPrimarySkills?.length > 0 ||
                    transformedData.competencySecondarySkills?.length > 0 ||
                    transformedData.questionnaire?.length > 0;

                if (hasAppraisalData) {
                    setEmployeeData(transformedData.employee);
                    setAppraisalData(transformedData);
                } else {
                    setEmployeeData(null);
                    setAppraisalData(null);
                }
            } else {
                showNotification("No data received from API", "error");
            }
        } catch (error) {
            console.error("Error fetching appraisal details:", error);
            showNotification(error.response?.data?.message || "Failed to load appraisal details", "error");
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (message, type) => {
        setNotification({ open: true, message, type });
        setTimeout(() => setNotification({ open: false, message: "", type: "" }), 4000);
    };

    const handleTabChange = (event, newValue) => setTabValue(newValue);

    const renderSkillsTable = (skills, title) => {
        if (!skills?.length) {
            return <Alert severity="warning" sx={{ my: 0.5 }}><MDTypography variant="body2">No {title} Added.</MDTypography></Alert>;
        }

        const COL = { num: 0.4, skill: 2.2, empScore: 1.2, empComment: 3.4, mgrScore: 1.2, mgrComment: 3.6 };
        const headerCellSx = { fontSize: "0.72rem", fontWeight: 700, color: "#444", lineHeight: 1.2 };

        return (
            <Box sx={{ border: "1px solid #ddd", borderRadius: 1, overflow: "hidden", mb: 1 }}>
                {/* Header */}
                <Grid container sx={{ backgroundColor: "#f0f4f8", px: 0.75, py: 0.5 }} alignItems="center">
                    <Grid item xs={COL.num} sx={colStyle}><Typography sx={headerCellSx}>#</Typography></Grid>
                    <Grid item xs={COL.skill} sx={colStyle}><Typography sx={headerCellSx}>Skill Set</Typography></Grid>
                    <Grid item xs={COL.empScore} sx={{ ...colStyle, justifyContent: "center" }}>
                        <Typography sx={{ ...headerCellSx, textAlign: "center" }}>Emp. Score</Typography>
                    </Grid>
                    <Grid item xs={COL.empComment} sx={colStyle}>
                        <Typography sx={headerCellSx}>Emp. Comments</Typography>
                    </Grid>
                    <Grid item xs={COL.mgrScore} sx={{ ...colStyle, justifyContent: "center" }}>
                        <Typography sx={{ ...headerCellSx, textAlign: "center" }}>Appraiser Score</Typography>
                    </Grid>
                    <Grid item xs={COL.mgrComment} sx={colStyle}>
                        <Typography sx={headerCellSx}>Appraiser Comment</Typography>
                    </Grid>
                </Grid>

                {/* Rows */}
                {skills.map((skill, index) => (
                    <Grid
                        container
                        key={index}
                        alignItems="center"
                        sx={{
                            px: 0.75,
                            py: 0.25,
                            backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                            borderTop: "1px solid #eee",
                            "&:hover": { backgroundColor: "#f5f9ff" },
                            minHeight: ROW_HEIGHT,
                        }}
                    >
                        <Grid item xs={COL.num} sx={colStyle}>
                            <Typography variant="body2" sx={{ fontSize: "0.78rem", color: "#666" }}>{index + 1}</Typography>
                        </Grid>
                        <Grid item xs={COL.skill} sx={colStyle}>
                            <Typography variant="body2" sx={{ fontSize: "0.8rem", wordBreak: "break-word", lineHeight: 1.3 }}>
                                {skill.skillSet || skill.skillName}
                            </Typography>
                        </Grid>
                        <Grid item xs={COL.empScore} sx={{ ...colStyle, justifyContent: "center" }}>
                            <FormControl size="small" disabled sx={{ width: 68 }}>
                                <Select value={skill.empScore || ""} displayEmpty sx={scoreSx}>
                                    <MenuItem value="" sx={{ fontSize: "0.8rem" }}>-</MenuItem>
                                    {[1, 2, 3, 4, 5].map(s => <MenuItem key={s} value={s} sx={{ fontSize: "0.8rem" }}>{s}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={COL.empComment} sx={colStyle}>
                            <Tooltip
                                title={skill.empComments || skill.empComment || ""}
                                placement="top"
                                arrow
                                disableHoverListener={!skill.empComments && !skill.empComment}
                            >
                                <span style={{ width: "100%" }}>
                                    <TextField
                                        fullWidth
                                        value={skill.empComments || skill.empComment || ""}
                                        disabled size="small" variant="outlined" placeholder="-"
                                        sx={commentSx} inputProps={{ style: { fontSize: "0.8rem" } }}
                                    />
                                </span>
                            </Tooltip>
                        </Grid>
                        <Grid item xs={COL.mgrScore} sx={{ ...colStyle, justifyContent: "center" }}>
                            <FormControl size="small" disabled sx={{ width: 68 }}>
                                <Select value={skill.appraiserScore || ""} displayEmpty sx={scoreSx}>
                                    <MenuItem value="" sx={{ fontSize: "0.8rem" }}>-</MenuItem>
                                    {[1, 2, 3, 4, 5].map(s => <MenuItem key={s} value={s} sx={{ fontSize: "0.8rem" }}>{s}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={COL.mgrComment} sx={colStyle}>
                            <Tooltip
                                title={skill.appraiserComments || skill.appraiserComment || ""}
                                placement="top"
                                arrow
                                disableHoverListener={!skill.appraiserComments && !skill.appraiserComment}
                            >
                                <span style={{ width: "100%" }}>
                                    <TextField
                                        fullWidth
                                        value={skill.appraiserComments || skill.appraiserComment || ""}
                                        disabled size="small" variant="outlined" placeholder="-"
                                        sx={commentSx} inputProps={{ style: { fontSize: "0.8rem" } }}
                                    />
                                </span>
                            </Tooltip>
                        </Grid>
                    </Grid>
                ))}
            </Box>
        );
    };

    const renderQuestionnaire = (questionnaire) => {
        if (!questionnaire?.length) {
            return <Alert severity="warning"><MDTypography variant="body2">No Questionnaire Added.</MDTypography></Alert>;
        }
        return (
            <MDBox>
                {questionnaire.map((item, index) => (
                    <MDBox key={index} mb={0.75}>
                        <MDTypography variant="body2" mb={0.25} color="dark" fontWeight="medium" sx={{ fontSize: "0.82rem" }}>
                            {item.question}
                        </MDTypography>
                        <TextField
                            fullWidth multiline rows={2}
                            value={item.description || ""}
                            disabled variant="outlined" size="small"
                            placeholder="No answer provided"
                            sx={{
                                "& .MuiInputBase-input": { color: "#000 !important", fontSize: "0.82rem" },
                                "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#000 !important" },
                            }}
                        />
                    </MDBox>
                ))}
            </MDBox>
        );
    };

    const InfoField = ({ label, value, xs = 12, sm = 6, md = 3 }) => (
        <Grid item xs={xs} sm={sm} md={md}>
            <MDBox
                px={0.75} py={0.4}
                sx={{
                    backgroundColor: '#fff',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    minHeight: 38,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#888", textTransform: "uppercase", mb: 0.1, lineHeight: 1.2 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#222", lineHeight: 1.3 }}>
                    {value || '-'}
                </Typography>
            </MDBox>
        </Grid>
    );

    if (loading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                    <CircularProgress />
                </MDBox>
            </DashboardLayout>
        );
    }

    if (!employeeData || !appraisalData || employeeData.empPermission) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={1} pb={1}>
                    <Card>
                        <MDBox p={1.5}>
                            <Alert severity="info">
                                <MDTypography variant="subtitle2" fontWeight="medium" mb={0.5}>
                                    No Appraisal Data Available
                                </MDTypography>
                                <MDTypography variant="body2" sx={{ fontSize: "0.82rem" }}>
                                    This employee has not completed their self-appraisal yet, or no appraisal data is available for the current period.
                                </MDTypography>
                                <MDBox mt={1}>
                                    <MDButton variant="gradient" color="info" size="small" onClick={() => navigate(-1)} sx={{ background: properties.colors.bgcolorbluethem }}>
                                        Go Back
                                    </MDButton>
                                </MDBox>
                            </Alert>
                        </MDBox>
                    </Card>
                </MDBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={0.5} pb={1.5}>
                <Card>
                    {/* Header */}
                    <MDBox
                        variant="gradient"
                        sx={{ background: properties.colors.bgcolorbluethem }}
                        bgColor="info"
                        borderRadius="lg"
                        coloredShadow="success"
                        mx={1.5}
                        mt={0.75}
                        p={0.75}
                        textAlign="center"
                    >
                        <MDTypography variant="h5" fontWeight="medium" color="white" sx={{ fontSize: "1rem" }}>
                            View Employee Ratings
                        </MDTypography>
                        <MDTypography variant="body2" color="white" opacity={0.85} sx={{ fontSize: "0.75rem" }}>
                            Review employee self-appraisal and manager ratings
                        </MDTypography>
                    </MDBox>

                    <MDBox p={0.75}>
                        {/* Employee Official Details */}
                        <Card variant="outlined" sx={{ mb: 0.75 }}>
                            <MDBox px={0.75} pt={0.5} pb={0.25} sx={{ backgroundColor: "#f7f7f7", borderBottom: "1px solid #e0e0e0" }}>
                                <MDTypography variant="subtitle2" fontWeight="bold" sx={{ fontSize: "0.82rem" }}>
                                    Official Details
                                </MDTypography>
                            </MDBox>
                            <MDBox px={0.75} py={0.5}>
                                <Grid container spacing={0.5}>
                                    <InfoField label="Employee ID" value={employeeData.empId} xs={12} sm={6} md={3} />
                                    <InfoField label="First Name" value={employeeData.firstName} xs={12} sm={6} md={3} />
                                    <InfoField label="Middle Name" value={employeeData.middleName} xs={12} sm={6} md={3} />
                                    <InfoField label="Last Name" value={employeeData.lastName} xs={12} sm={6} md={3} />
                                    <InfoField label="Gender" value={employeeData.gender} xs={12} sm={6} md={3} />
                                    <InfoField
                                        label="Date of Joining"
                                        value={employeeData.dateOfJoining ? new Date(employeeData.dateOfJoining).toLocaleDateString() : '-'}
                                        xs={12} sm={6} md={3}
                                    />
                                    <InfoField label="Total Experience (Yrs)" value={employeeData.totalExperience} xs={12} sm={6} md={3} />
                                    <InfoField
                                        label="Reporting Since"
                                        value={employeeData.reportingSince}
                                        xs={12} sm={6} md={3}
                                    />
                                </Grid>
                            </MDBox>
                        </Card>

                        {/* Ratings Legend */}
                        <Card variant="outlined" sx={{ mb: 0.75 }}>
                            <MDBox px={0.75} pt={0.5} pb={0.25} sx={{ backgroundColor: "#f7f7f7", borderBottom: "1px solid #e0e0e0" }}>
                                <MDTypography variant="subtitle1" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                                    Performance Appraisal Form (Ratings)
                                </MDTypography>
                            </MDBox>
                            <MDBox px={0.75} py={0.5}>
                                <MDTypography variant="body2" mb={0.5} color="black" sx={{ fontSize: "0.78rem" }}>
                                    Use the following ratings for consistency.
                                </MDTypography>
                                <Grid container sx={{ backgroundColor: "#f0f4f8", px: 0.5, py: 0.3, borderRadius: "4px 4px 0 0", border: "1px solid #ddd" }}>
                                    <Grid item xs={1}><Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Points</Typography></Grid>
                                    <Grid item xs={2.5}><Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Ratings</Typography></Grid>
                                    <Grid item xs={8.5}><Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Comments</Typography></Grid>
                                </Grid>
                                {RATING_LEGEND.map((row, index) => (
                                    <Grid
                                        container key={row.points}
                                        sx={{
                                            px: 0.5, py: 0.2,
                                            backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                                            border: "1px solid #ddd", borderTop: "none",
                                            borderRadius: index === RATING_LEGEND.length - 1 ? "0 0 4px 4px" : 0,
                                        }}
                                        alignItems="center"
                                    >
                                        <Grid item xs={1}><Typography sx={{ fontSize: "0.78rem" }}>{row.points}</Typography></Grid>
                                        <Grid item xs={2.5}><Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#222" }}>{row.rating}</Typography></Grid>
                                        <Grid item xs={8.5}><Typography sx={{ fontSize: "0.78rem", color: "#333" }}>{row.comment}</Typography></Grid>
                                    </Grid>
                                ))}
                            </MDBox>
                        </Card>

                        {/* Tabs */}
                        <Card variant="outlined" sx={{ mb: 0.75 }}>
                            <Box sx={{ borderBottom: "1px solid #ddd" }}>
                                <Tabs
                                    value={tabValue}
                                    onChange={handleTabChange}
                                    textColor="info"
                                    indicatorColor="info"
                                    variant="fullWidth"
                                    sx={{
                                        minHeight: 38,
                                        "& .MuiTab-root": { textTransform: "none", fontWeight: 500, fontSize: "0.82rem", minHeight: 38, py: 0 },
                                        "& .Mui-selected": { color: "#2196f3 !important" },
                                    }}
                                >
                                    <Tab label="Technical Skills" />
                                    <Tab label="Performance Competency" />
                                    <Tab label="Questionnaire" />
                                </Tabs>
                            </Box>

                            <TabPanel value={tabValue} index={0}>
                                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.25} sx={{ fontSize: "0.82rem" }}>
                                    Technical Primary Skills
                                </MDTypography>
                                {renderSkillsTable(appraisalData.technicalPrimarySkills, "Technical Primary Skills")}
                                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.25} sx={{ fontSize: "0.82rem" }}>
                                    Technical Secondary Skills
                                </MDTypography>
                                {renderSkillsTable(appraisalData.technicalSecondarySkills, "Technical Secondary Skills")}
                            </TabPanel>

                            <TabPanel value={tabValue} index={1}>
                                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.25} sx={{ fontSize: "0.82rem" }}>
                                    Competency Primary Skills
                                </MDTypography>
                                {renderSkillsTable(appraisalData.competencyPrimarySkills, "Competency Primary Skills")}
                                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.25} sx={{ fontSize: "0.82rem" }}>
                                    Competency Secondary Skills
                                </MDTypography>
                                {renderSkillsTable(appraisalData.competencySecondarySkills, "Competency Secondary Skills")}
                            </TabPanel>

                            <TabPanel value={tabValue} index={2}>
                                <MDTypography variant="subtitle2" fontWeight="bold" mb={0.5} sx={{ fontSize: "0.82rem" }}>
                                    Questionnaire
                                </MDTypography>
                                {renderQuestionnaire(appraisalData.questionnaire)}
                            </TabPanel>
                        </Card>

                        {/* Back Button */}
                        <MDBox display="flex" justifyContent="flex-end" mt={0.5}>
                            <MDButton variant="outlined" color="secondary" size="small" onClick={() => navigate(-1)}>
                                Back
                            </MDButton>
                        </MDBox>
                    </MDBox>
                </Card>

                {/* Notification */}
                {notification.open && (
                    <MDBox sx={{ position: "fixed", top: 16, right: 16, zIndex: 9999, minWidth: 280 }}>
                        <Alert severity={notification.type} onClose={() => setNotification({ open: false, message: "", type: "" })} sx={{ fontSize: "0.82rem" }}>
                            {notification.message}
                        </Alert>
                    </MDBox>
                )}
            </MDBox>
        </DashboardLayout>
    );
}

export default ViewEmployeeRatings;